import { useDispatch, useSelector } from 'react-redux';
import { CookiesKey, EndpointsMvp } from './enum';
import { useAuth } from './useAuth';
import { EToastTypes } from '../../store/ducks/toast/types';
import { addToast } from '../../store/ducks/toast/actions';
import { deleteCookie, getValueCookie } from './utils';
import { clearTipoCompraMap } from './tipoCompraMap';
import { GetFlag } from '../../utils/featureFlag';
import {
  BulkCartResponse,
  CartItemPayload,
  CartOptions,
  CartProps,
  MinimumOrderItemProps,
  NewCartItem,
  NewCartOption,
  SearchResponse
} from './models';
import { ApplicationState } from '../../store';
import { updateCartItemsQuantity } from '../../store/ducks/cart-items-mvp/actions';
import { ICustomer } from '../../store/ducks/customers/types';
import { useShoppingCart } from '../../providers/shoppingcart.provider';
import { useApiInstance } from './useApiInstance';
interface customerTypeMvpProps extends ICustomer {
  customerType: string;
}

interface UnexpectedErrorProps {
  parameters?: {
    fieldName?: 'cartId';
  };
}

export interface ParamsOptionsProps {
  withPrice?: boolean;
  withOptions?: boolean;
}

export const UnexpectedError = (error: UnexpectedErrorProps) => {
  if (error.parameters?.fieldName === 'cartId') {
    deleteCookie('@cart');
  }

  throw Error();
};

let createNewCartErrorToastPending = false;

export const useCartService = () => {
  const { updateProductsInformation } = useShoppingCart();
  const { getHeaders } = useAuth();
  const commerceApi = useApiInstance();
  const headers = getHeaders();
  const newCartEndpoint = GetFlag('new_cart_endpoints');

  const shoppingCart = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const dispatch = useDispatch();

  const handleUpdateQuantity = (qty: number) => {
    dispatch(updateCartItemsQuantity(qty));
  };

  const deleteCartId = () => {
    handleUpdateQuantity(0);
    deleteCookie(CookiesKey.CART_ID);
    deleteCookie(CookiesKey.EMITTER_ID);
    deleteCookie(CookiesKey.RECEIVER_ID);
    clearTipoCompraMap();
  };

  const createNewCart = async (
    emitterId = '',
    receiverId = '',
    newCart = false,
    settingsOrderCheck = false,
    enableToastError = true,
    throwOnError = false
  ) => {
    const soldTo = emitterId || shoppingCart.emitterId;
    const shipTo = receiverId || shoppingCart.receiverId;
    const bodyRequest = {
      soldTo: soldTo,
      shipTo: shipTo
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    if (hasBodyKeyEmpty) {
      return null;
    }

    const existingCart = getCartId(emitterId, receiverId);
    const isSameReceiver = getValueCookie(CookiesKey.RECEIVER_ID) === shipTo;
    if (existingCart && existingCart.length > 0 && isSameReceiver && !newCart) {
      const cartQuantity = settingsOrderCheck
        ? await getCartItemQuantity({
            alreadyItems: undefined,
            countType: true,
            soldTo,
            shipTo
          })
        : await getCartItemQuantity({ soldTo, shipTo });
      handleUpdateQuantity(cartQuantity);
      return {
        cartId: existingCart,
        alreadyCart: cartQuantity > 0,
        cartQuantity: cartQuantity ?? 0
      };
    }
    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.post(
          `${EndpointsMvp.CART_MINE}`,
          {},
          {
            headers: getHeaders({ withCommerceToken: true }),
            params: {
              soldTo: bodyRequest.soldTo,
              shipTo: bodyRequest.shipTo
            }
          }
        );
      } else {
        // Legacy endpoint create cart
        response = await commerceApi.post(
          `${EndpointsMvp.CREATE_CART}`,
          {
            ...bodyRequest
          },
          { headers }
        );
      }

      deleteCookie(CookiesKey.CART_ID);
      deleteCookie(CookiesKey.EMITTER_ID);
      deleteCookie(CookiesKey.RECEIVER_ID);

      if (!isNaN(Number(response.data))) {
        document.cookie = `@cart=${response.data};  path=/`;
        document.cookie = `@emitter=${soldTo};  path=/`;
        document.cookie = `${CookiesKey.RECEIVER_ID}=${shipTo};  path=/`;
        // setCookieWithExpiry({ name: '@ordercontinuos', value: true, hours: 2 });
      }

      if (newCartEndpoint) {
        const cartQuantity = await getCartItemQuantity({
          alreadyItems: undefined,
          countType: true,
          soldTo,
          shipTo
        });
        handleUpdateQuantity(cartQuantity);
        const cartId = response.data;

        return {
          cartId,
          alreadyCart: cartQuantity > 0,
          cartQuantity: cartQuantity
        };
      } else {
        handleUpdateQuantity(0);
        const cartId = response.data;

        return { cartId, alreadyCart: false, cartQuantity: 0 };
      }
    } catch (error) {
      if (throwOnError) {
        throw error;
      }
      if (!enableToastError || createNewCartErrorToastPending) {
        return;
      }
      createNewCartErrorToastPending = true;
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao criar novo pedido',
          durationInMS: 5000
        })
      );
      setTimeout(() => {
        createNewCartErrorToastPending = false;
      }, 5000);
    }
  };

  const addCartItem = async (
    item: CartItemPayload,
    quantity?: number,
    paramsOptions?: ParamsOptionsProps,
    soldToOverride?: string,
    shipToOverride?: string
  ) => {
    const bodyRequest = {
      soldTo: soldToOverride || shoppingCart.emitterId,
      shipTo: shipToOverride || shoppingCart.receiverId
    };
    const params = {
      includeOptions: paramsOptions?.withOptions
        ? paramsOptions?.withOptions
        : false,
      includePrices: paramsOptions?.withPrice ? paramsOptions?.withPrice : false
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    const cartId = getCartId(soldToOverride ?? '', shipToOverride ?? '');
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }

    const formatedItem = { ...item } as Partial<
      Pick<
        NewCartItem,
        | 'price'
        | 'item_id'
        | 'media_gallery_entries'
        | 'errors'
        | 'cartOptions'
        | 'sap_descricao_gpm'
        | 'descricao_gpm'
      >
    >;

    delete formatedItem.media_gallery_entries;
    delete formatedItem.errors;
    delete formatedItem.item_id;
    delete formatedItem.price;
    delete formatedItem.cartOptions;
    delete formatedItem.sap_descricao_gpm;
    delete formatedItem.descricao_gpm;

    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.post(
          `${EndpointsMvp.CART_MINE_ITEMS}`,
          { ...bodyRequest, cartItem: formatedItem },
          {
            headers: {
              ...getHeaders({ withCommerceToken: true }),
              'ship-to': bodyRequest.shipTo,
              'sold-to': bodyRequest.soldTo
            }
          }
        );
      } else {
        response = await commerceApi.post(
          `${EndpointsMvp.ADD_ITEM(cartId)}`,
          { ...bodyRequest, cartItem: formatedItem },
          { headers, params }
        );
      }

      if (response.data?.error) {
        if (
          ['The cart was already closed', 'No such entity'].includes(
            response.data.error?.message ?? ''
          )
        ) {
          // if error has cart expiration
          handleUpdateQuantity(0);
          const data = await recursiveAddCartItem(
            item,
            quantity,
            paramsOptions
          );
          return data;
        }
        UnexpectedError(response.data.error);
      }

      if (quantity && quantity >= 0) {
        handleUpdateQuantity(cartQuantity + 1);
      } else {
        const cartQuantity = await getCartItemQuantity();
        handleUpdateQuantity(cartQuantity);
      }

      return response.data;
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao adicionar item ao carrinho',
          durationInMS: 5000
        })
      );
    }
  };

  const recursiveAddCartItem = async (
    item: CartItemPayload,
    quantity?: number,
    paramsOptions?: ParamsOptionsProps
  ) => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const params = {
      includeOptions: paramsOptions?.withOptions
        ? paramsOptions?.withOptions
        : false,
      includePrices: paramsOptions?.withPrice ? paramsOptions?.withPrice : false
    };

    const cartId = await createNewCart('', '', true);

    const formatedItem = { ...item } as Partial<
      Pick<
        NewCartItem,
        | 'price'
        | 'item_id'
        | 'media_gallery_entries'
        | 'errors'
        | 'cartOptions'
        | 'sap_descricao_gpm'
        | 'descricao_gpm'
      >
    >;

    delete formatedItem.media_gallery_entries;
    delete formatedItem.errors;
    delete formatedItem.item_id;
    delete formatedItem.price;
    delete formatedItem.cartOptions;
    delete formatedItem.sap_descricao_gpm;
    delete formatedItem.descricao_gpm;

    try {
      const response = await commerceApi.post(
        `${EndpointsMvp.ADD_ITEM(cartId?.cartId)}`,
        {
          ...bodyRequest,
          cartItem: { ...formatedItem, quote_id: cartId?.cartId }
        },
        { headers, params }
      );

      if (response.data?.error) {
        if (response.data.error?.message?.includes('No such entity')) {
          handleUpdateQuantity(0);
        }
        UnexpectedError(response.data.error);
      }

      if (quantity && quantity >= 0) {
        handleUpdateQuantity(cartQuantity + 1);
      } else {
        const cartQuantity = await getCartItemQuantity();
        handleUpdateQuantity(cartQuantity);
      }

      return response.data;
    } catch (error) {
      console.error(error);
      handleUpdateQuantity(0);
      deleteCookie('@cart');
    }
  };

  const updateCartItem = async ({ item }: { item: NewCartItem }) => {
    if (!item || !item.item_id) {
      return undefined;
    }

    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    if (hasBodyKeyEmpty) {
      return undefined;
    }

    const itemTaxBenefit = item?.extension_attributes?.tax_benefit_gerdau;
    const tax_benefit_gerdau =
      !itemTaxBenefit || itemTaxBenefit === 'nenhum' ? '' : itemTaxBenefit;

    const cartId = getCartId();

    const updatedItem = {
      sku: item.sku,
      qty: item.qty,
      quote_id: cartId,
      extension_attributes: {
        measure_unity_gerdau: item.extension_attributes.measure_unity_gerdau,
        destination_gerdau: item.extension_attributes.destination_gerdau,
        payment_type_gerdau: item.extension_attributes.payment_type_gerdau,
        has_agreed_price_gerdau:
          item.extension_attributes.has_agreed_price_gerdau ?? false,
        sap_fields: item.extension_attributes.sap_fields ?? {
          o_header_sap: [],
          o_item_sap: [],
          o_price_sap: []
        },
        tax_benefit_gerdau: tax_benefit_gerdau,
        ...(item.extension_attributes.expected_date_gerdau && {
          expected_date_gerdau: item.extension_attributes.expected_date_gerdau
        })
      }
    };

    try {
      const response = await commerceApi.put(
        `${EndpointsMvp.CART_ITEMS_MINE_UPDATE(item.item_id)}`,
        { ...bodyRequest, cartItem: updatedItem },
        {
          headers: {
            ...getHeaders({ withCommerceToken: true }),
            'ship-to': bodyRequest.shipTo,
            'sold-to': bodyRequest.soldTo
          }
        }
      );

      if (response.data?.error) {
        UnexpectedError(response.data.error);
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: `Falha ao atualizar item no carrinho`,
          durationInMS: 5000
        })
      );
    }
  };

  const addBulkCartItem = async ({
    items,
    withPrice = true,
    withOptions = true,
    noCheckPrice = false,
    complete = false,
    emitterId,
    customerId
  }: {
    items: NewCartItem[];
    withPrice?: boolean;
    withOptions?: boolean;
    noCheckPrice?: boolean;
    complete?: boolean;
    emitterId?: string;
    customerId?: string;
  }) => {
    if (!items) {
      return { items: [], errors: [] } as BulkCartResponse;
    }
    const bodyRequest = {
      soldTo: emitterId || shoppingCart.emitterId,
      shipTo: customerId || shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    let cartId = getCartId(bodyRequest.soldTo, bodyRequest.shipTo);

    if (!cartId) {
      const newCartId = await createNewCart(
        bodyRequest.soldTo,
        bodyRequest.shipTo
      );
      cartId = String(newCartId?.cartId ?? '');
    }

    if (!cartId || hasBodyKeyEmpty) {
      return { items: [], errors: [] } as BulkCartResponse;
    }

    const params = {
      includeOptions: withOptions,
      includePrices: withPrice
    };

    const newItems = [...items]
      .map((item) => {
        const itemTaxBenefit = item?.extension_attributes?.tax_benefit_gerdau;

        const tax_benefit_gerdau =
          !itemTaxBenefit || itemTaxBenefit === 'nenhum' ? '' : itemTaxBenefit;

        let extension_attributes = {};

        if (!newCartEndpoint) {
          extension_attributes = {
            ...(item?.extension_attributes ?? {}),
            tax_benefit_gerdau: tax_benefit_gerdau
          };
        } else {
          extension_attributes = {
            item_id_gerdau: item.extension_attributes.item_id_gerdau,
            measure_unity_gerdau:
              item.extension_attributes.measure_unity_gerdau,
            destination_gerdau: item.extension_attributes.destination_gerdau,
            payment_type_gerdau: item.extension_attributes.payment_type_gerdau,
            sap_fields: item.extension_attributes.sap_fields ?? {
              o_header_sap: [],
              o_item_sap: [],
              o_price_sap: []
            },
            tax_benefit_gerdau: tax_benefit_gerdau,
            ...(item.extension_attributes.expected_date_gerdau && {
              expected_date_gerdau:
                item.extension_attributes.expected_date_gerdau
            }),
            ...(item.extension_attributes.estimated_date && {
              estimated_date: item.extension_attributes.estimated_date
            })
          };
        }

        return {
          ...(item.item_id && { item_id: item.item_id }),
          sku: item.sku,
          qty: item.qty,
          quote_id: cartId,
          extension_attributes: { ...extension_attributes }
        };
      })
      .slice(0, 30);

    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.post(
          `${EndpointsMvp.CART_MINE_ITEMS_BULK}`,
          {
            soldTo: bodyRequest.soldTo,
            shipTo: bodyRequest.shipTo,
            cartItems: [...newItems]
          },
          {
            headers: {
              ...getHeaders({ withCommerceToken: true }),
              'ship-to': bodyRequest.shipTo,
              'sold-to': bodyRequest.soldTo
            }
          }
        );
      } else {
        response = await commerceApi.post(
          `${EndpointsMvp.ADD_ITEM_BULK(cartId)}`,
          { ...bodyRequest, items: [...newItems] },
          { headers, params }
        );
      }
      if (!response || !response.data) {
        UnexpectedError(response?.data?.error);
      }

      if (!noCheckPrice) {
        const productsWithPrice =
          (response &&
            response.data &&
            response.data.items &&
            response.data.items.filter(
              (product: NewCartItem) =>
                Number(product?.price?.creditPrice ?? 0) > 0
            )) ??
          [];

        handleUpdateQuantity(productsWithPrice.length ?? 0);
      }

      if (complete || newCartEndpoint) {
        return response.data;
      }

      return {
        items: response.data.items as NewCartItem[],
        errors: [] as string[]
      };
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: `Falha ao atualizar itens no carrinho`,
          durationInMS: 5000
        })
      );
      return { items: [], errors: [] } as BulkCartResponse;
    }
  };

  const getCartId = (customerId = '', receiverId = '') => {
    const cartId = getValueCookie(CookiesKey.CART_ID);
    const emitterId = getValueCookie(CookiesKey.EMITTER_ID);
    const cartReceiverId = getValueCookie(CookiesKey.RECEIVER_ID);

    const isSameReceiver =
      !cartReceiverId ||
      shoppingCart.receiverId === cartReceiverId ||
      (receiverId && receiverId === cartReceiverId);

    if (
      emitterId &&
      cartId &&
      isSameReceiver &&
      (shoppingCart.emitterId === emitterId ||
        (customerId && customerId === emitterId))
    ) {
      return cartId;
    }
    return '';
  };

  const getCartIdOrGenerateOne = async () => {
    const cartId = getCartId();
    if (!cartId || !cartId.length) {
      return createNewCart();
    }
    return cartId ?? '';
  };

  const getMinimunOrder = async (
    alreadyFetchedItems?: NewCartItem[]
  ): Promise<{ items?: MinimumOrderItemProps[] } | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    const customer = customersState.customers.find(
      (customer) => customer.customerId === shoppingCart.emitterId
    ) as customerTypeMvpProps;
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      let response;

      if (newCartEndpoint) {
        const cartItems = alreadyFetchedItems ?? (await getCartItems());

        const productList = (cartItems || []).map((item) => ({
          sku: item.sku,
          custom_attributes: [
            {
              attribute_code: 'sap_cod_gpd',
              value: item.extension_attributes?.gpd || ''
            },
            {
              attribute_code: 'sap_descricao_gpm',
              value: item.extension_attributes?.gpm || ''
            }
          ]
        }));

        const requestBody = {
          productList,
          userBusinessType: customer?.customerType ?? '',
          customerId: bodyRequest.soldTo
        };

        response = await commerceApi.post(
          `${EndpointsMvp.NEW_MINIMUM_ORDER}`,
          requestBody,
          {
            headers: getHeaders({ withCommerceToken: false }),
            params: {
              soldTo: bodyRequest.soldTo,
              shipTo: bodyRequest.shipTo
            }
          }
        );
      } else {
        response = await commerceApi.get(
          `${EndpointsMvp.MINIMUN_ORDER(cartId)}`,
          {
            headers: { ...headers, businessType: customer?.customerType ?? '' },
            params: bodyRequest
          }
        );
      }

      if (response.data && response.data?.message) {
        throw new Error();
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar dados de pedido minimo',
          durationInMS: 5000
        })
      );

      return undefined;
    }
  };

  const getCart = async (): Promise<CartProps | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await commerceApi.get(`${EndpointsMvp.CART(cartId)}`, {
        headers,
        params: bodyRequest
      });

      if (response.data?.error) {
        UnexpectedError(response.data.error);
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar pedido',
          durationInMS: 5000
        })
      );

      return undefined;
    }
  };

  const getPaymentCondition = async (): Promise<string | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    if (hasBodyKeyEmpty) {
      return undefined;
    }

    try {
      const paymentConditionResponse = await commerceApi.get(
        `${EndpointsMvp.PAYMENT_CONDITION({
          customer: shoppingCart.emitterId
        })}`,
        {
          headers: getHeaders({ withCommerceToken: true }),
          params: bodyRequest
        }
      );

      if (
        !paymentConditionResponse ||
        !paymentConditionResponse.data ||
        !paymentConditionResponse.data.paymentCondition
      ) {
        throw new Error();
      }

      return paymentConditionResponse.data.paymentCondition;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar condição de pagamento',
          durationInMS: 5000
        })
      );

      return undefined;
    }
  };

  const getCartItemQuantity = async ({
    alreadyItems = undefined,
    countType = false,
    soldTo,
    shipTo,
    paramsOptions = {
      withOptions: true,
      withPrice: true
    }
  }: {
    alreadyItems?: NewCartItem[];
    countType?: boolean;
    soldTo?: string;
    shipTo?: string;
    paramsOptions?: ParamsOptionsProps;
  } = {}): Promise<number> => {
    const finalSoldTo = soldTo || shoppingCart.emitterId;
    const finalShipTo = shipTo || shoppingCart.receiverId;
    const params = {
      soldTo: finalSoldTo,
      shipTo: finalShipTo,
      includeOptions: paramsOptions?.withOptions ?? true,
      includePrices: paramsOptions?.withPrice ?? true
    };

    const hasBodyKeyEmpty = Object.values(params).some((item) => !item);
    const cartId = getCartId(finalSoldTo, finalShipTo);
    if (!cartId || hasBodyKeyEmpty) {
      return 0;
    }

    if (alreadyItems) {
      const products = alreadyItems as NewCartItem[];

      const productsWithPrice = products.filter(
        (product) => Number(product.price.creditPrice) > 0
      );

      const cartQuantity = productsWithPrice.length ?? 0;
      return cartQuantity;
    }

    try {
      if (countType) {
        if (newCartEndpoint) {
          const response = await commerceApi.get(
            `${EndpointsMvp.CART_MINE_COUNT}`,
            {
              headers: getHeaders({ withCommerceToken: true }),
              params: {
                soldTo: finalSoldTo,
                shipTo: finalShipTo
              }
            }
          );

          return response.data || 0;
        } else {
          const response = await commerceApi.get(
            `${EndpointsMvp.CART_ITEM_QUANTITY(cartId)}`,
            {
              headers,
              params: {
                soldTo: finalSoldTo,
                shipTo: finalShipTo
              }
            }
          );

          if (response.data && response.data.count) {
            return response.data.count;
          }

          return 0;
        }
      } else {
        const response = await commerceApi.get(
          `${EndpointsMvp.GET_ITEMS(cartId)}`,
          { headers, params }
        );

        if (response.data && response.data.length > 0) {
          const products = response.data as NewCartItem[];

          const productsWithPrice = products.filter(
            (product) => Number(product.price.creditPrice) > 0
          );
          return productsWithPrice.length ?? 0;
        }

        return 0;
      }
    } catch (error) {
      //Create a new cart when has error on get quantity
      await createNewCart(finalSoldTo, finalShipTo, true);

      return 0;
    }
  };

  const getCartItems = async (): Promise<NewCartItem[] | undefined> => {
    const params = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId,
      includePrices: true,
      includeOptions: true
    };
    const hasBodyKeyEmpty = Object.values(params).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.get(`${EndpointsMvp.CART_MINE_ITEMS}`, {
          headers: getHeaders({ withCommerceToken: true }),
          params: {
            soldTo: params.soldTo,
            shipTo: params.shipTo
          }
        });
      } else {
        response = await commerceApi.get(`${EndpointsMvp.GET_ITEMS(cartId)}`, {
          headers,
          params
        });
      }

      if (response.data?.error) {
        UnexpectedError(response.data.error);
      }

      // Transform new API response to match old structure
      if (newCartEndpoint) {
        const transformedData = response.data.map((item: NewCartItem) => {
          const transformedItem = { ...item };

          transformedItem['price'] = JSON.parse(
            item.extension_attributes?.price_gerdau ?? '[]'
          );
          const productOptions = JSON.parse(
            item.extension_attributes?.product_options ?? '[]'
          );

          const cartOptions: NewCartOption = {};
          productOptions.forEach(
            (options: {
              destinations: Array<
                CartOptions & { condGroup: string; description: string }
              >;
            }) => {
              options.destinations.forEach((dest) => {
                cartOptions[dest.condGroup] = {
                  ...dest,
                  destination: {
                    code: dest.condGroup,
                    description: dest.description
                  }
                };
              });
            }
          );

          transformedItem['cartOptions'] = cartOptions;

          return transformedItem;
        });

        return transformedData;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  };

  const getCartProductsInformation = async (
    products: string[]
  ): Promise<SearchResponse[] | undefined> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);
    const cartId = getCartId();
    if (!cartId || hasBodyKeyEmpty) {
      return undefined;
    }
    try {
      const response = await commerceApi.get(
        `${EndpointsMvp.SEARCH_PRODUCTS(products)}`,
        { headers, params: bodyRequest }
      );

      if (response.data && Array.isArray(response.data)) {
        updateProductsInformation(response.data);
      }

      return response.data;
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar informações dos produtos',
          durationInMS: 5000
        })
      );
    }
  };

  const deleteAllItems = async ({
    updateQty = true
  }: {
    updateQty?: boolean;
  }) => {
    const cartId = getCartId();
    try {
      let response;

      if (newCartEndpoint) {
        const deactive = await deactivateCart();
        deleteCartId();

        return deactive;
      } else {
        // Legacy endpoint: DELETE_ITEMS
        response = await commerceApi.delete(
          `${EndpointsMvp.DELETE_ITEMS(cartId)}`,
          { headers }
        );

        if (updateQty) {
          handleUpdateQuantity(0);
        }

        return response.data as boolean;
      }
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao buscar itens do pedido',
          durationInMS: 5000
        })
      );
    }
  };

  const deleteItem = async (itemId: number) => {
    const cartId = getCartId();
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };

    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.post(
          `${EndpointsMvp.CART_MINE_ITEMS_BULK_DELETE}`,
          {
            soldTo: bodyRequest.soldTo,
            shipTo: bodyRequest.shipTo,
            items: [
              {
                item_id: itemId
              }
            ]
          },
          {
            headers: {
              ...getHeaders({ withCommerceToken: true })
            }
          }
        );
      } else {
        // Legacy endpoint: DELETE_ITEM
        response = await commerceApi.delete(
          `${EndpointsMvp.DELETE_ITEM(cartId, itemId)}`,
          { headers }
        );
      }

      if (response.status === 200) {
        handleUpdateQuantity(cartQuantity - 1);

        return response.data as boolean;
      }

      throw new Error();
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao deletar item do pedido',
          durationInMS: 5000
        })
      );
    }
  };

  const deleteBulkItems = async (itemIds: number[]) => {
    const cartId = getCartId();
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };

    try {
      let response;

      if (newCartEndpoint) {
        response = await commerceApi.post(
          `${EndpointsMvp.CART_MINE_ITEMS_BULK_DELETE}`,
          {
            soldTo: bodyRequest.soldTo,
            shipTo: bodyRequest.shipTo,
            items: itemIds.map((itemId) => ({ item_id: itemId }))
          },
          {
            headers: {
              ...getHeaders({ withCommerceToken: true })
            }
          }
        );
      } else {
        // Legacy: Delete items one by one
        const deletePromises = itemIds.map((itemId) =>
          commerceApi.delete(`${EndpointsMvp.DELETE_ITEM(cartId, itemId)}`, {
            headers
          })
        );
        await Promise.all(deletePromises);
        response = { status: 200, data: true };
      }

      if (response.status === 200) {
        handleUpdateQuantity(cartQuantity - itemIds.length);
        return response.data as boolean;
      }

      throw new Error();
    } catch (error) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Falha ao deletar itens do pedido',
          durationInMS: 5000
        })
      );
      return false;
    }
  };

  const deactivateCart = async (): Promise<boolean> => {
    const bodyRequest = {
      soldTo: shoppingCart.emitterId,
      shipTo: shoppingCart.receiverId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    if (hasBodyKeyEmpty) {
      return false;
    }

    try {
      await commerceApi.put(
        `${EndpointsMvp.CART_MINE_DEACTIVATE}`,
        {
          quote: { is_active: false },
          ...bodyRequest
        },
        {
          headers: {
            ...getHeaders({ withCommerceToken: true }),
            'ship-to': bodyRequest.shipTo,
            'sold-to': bodyRequest.soldTo
          }
        }
      );

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const warmup = async ({
    emitterId
  }: {
    emitterId?: string;
  }): Promise<boolean> => {
    const bodyRequest = {
      soldTo: emitterId,
      shipTo: emitterId
    };
    const hasBodyKeyEmpty = Object.values(bodyRequest).some((item) => !item);

    if (hasBodyKeyEmpty) {
      return false;
    }

    try {
      await commerceApi.get(
        `${EndpointsMvp.WARMUP({
          shipTo: bodyRequest.shipTo,
          soldTo: bodyRequest.soldTo
        })}`,
        {
          headers: {
            ...getHeaders({ withCommerceToken: true }),
            'ship-to': bodyRequest.shipTo,
            'sold-to': bodyRequest.soldTo
          }
        }
      );

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  return {
    handleUpdateQuantity,
    getCartId,
    getCartIdOrGenerateOne,
    createNewCart,
    getMinimunOrder,
    addCartItem,
    addBulkCartItem,
    updateCartItem,
    getCart,
    getCartItems,
    getCartItemQuantity,
    getCartProductsInformation,
    deleteItem,
    deleteBulkItems,
    deleteAllItems,
    getPaymentCondition,
    deactivateCart,
    warmup
  };
};
